/**
 * School View (v3.78.0) — pure derivation. No Firestore here.
 *
 * Input: the building's current-year classes, students and result docs (the
 * three collections the Results pages read, loaded by schoolViewData.js).
 * Output: ONE plain summary object shaped like the future summary callable
 * (docs/requirements/school-view.md §6) so item B (GRADE TEAM VIEW) can swap
 * the source without touching the UI:
 *
 *   { columns: [...],                       // every column present anywhere in the building (+ U/L/S)
 *     building: { studentCount, assessments: { [columnKey]: { administered, total, bands, legend, max } } },
 *     classes: [{ classId, displayName, grade, teacherName, studentCount,
 *       assessments: { [columnKey]: { administered, total, bands: { [bandKey]: n } } } }],
 *     grades: { [gradeKey]: { classCount, studentCount, classIds,
 *       assessments: { ...same shape... } } } }
 *
 *   A class or grade block carries only the columns its own students have a
 *   result for in the window (+ U/L/S always); `building` is the roll-up the
 *   legend reads (max + cut points per column).
 *
 * Rules baked in (owner decisions 2026-09-14):
 *   - Grouping key = class.grade (the enum label the Clever importer writes:
 *     Pre-K … 3rd Grade), ordered Pre-K → Kindergarten → 1st → 2nd → 3rd,
 *     then Early Childhood, Other, then "No grade set". A student whose own
 *     grade differs is kept under the CLASS grade and counted as a mismatch —
 *     never reassigned.
 *   - "Latest in window" = the newest attempt per (student, column): higher
 *     quarter label first, then timestamp (the Dashboard / Groups rule). A
 *     `correctCount: -1` (N/A) doc is NOT a result — not administered ≠ zero.
 *   - Bands come from bandForScore only (N bands per family, never a
 *     hardcoded three).
 *   - Column catalog is data-driven: Letter/Sound Uppercase / Lowercase /
 *     Sounds always; every other column appears when at least one student in
 *     the scope has a result in the window. Family order = sidebar order.
 */
import {
  NO_GRADE, gradeKeyOf, WINDOW_LATEST, quarterLabelsForWindow, columnKeyOf, pickLatestInWindow,
  fetchLabelsForWindow, resultsInWindow, isSeason, SEASONS, SEASON_OF_QUARTER, PAD_TEST_TYPE as PAD_KEY,
} from './reportCore';
import { isFeatureEnabled } from '../constants/featureFlags';
import { TEST_TYPE_LABELS } from '../constants/assessmentTypes';
import { PA4K_TEST_TYPES, PA4K_GROUP_NAME } from '../constants/pa4kItems';
import { PA_STEP_TYPES, PA_SCREENER_LABEL } from '../constants/paScreenerItems';
import { enabledVowelTestTypes, VOWEL_TEST_TYPES } from '../constants/vowelTests';
import { loadTier, PAD_TIER_IDS } from '../pa-diagnostic/lib/loadTier';
import { gradeToTier } from '../pa-diagnostic/lib/gradeToTier';
import { bandForScore, bandsForTestType, legendMaxFor, letterScaleNote, LETTER_TEST_TYPES, HFW_TEST_TYPE, CVC_TEST_TYPE, PAD_TEST_TYPE } from './bandForScore';
import { buildClassLabelMap, classLabelFrom } from './classDisplay';
import { lastNameComparator } from './sortByLastName';
import { studentDisplayName, safeCsvCell, formatDateForCsv } from './csv';

// ─── Grades ──────────────────────────────────────────────────────────────────

export const GRADE_ORDER = ['Pre-K', 'Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', 'Early Childhood', 'Other'];
// v3.80.0: the grade key, the windows, normalizeResult, columnKeyOf and
// pickLatestInWindow live in reportCore.js (dependency-free, shared with the
// schoolViewSummary callable through the generated functions/shared copy);
// re-exported here so every existing importer is untouched.
export { NO_GRADE, gradeKeyOf } from './reportCore';
export const NO_GRADE_LABEL = 'No grade set';
export const gradeLabel = (key) => (key === NO_GRADE ? NO_GRADE_LABEL : key);

export function gradeSortKey(key) {
  if (key === NO_GRADE) return [2, ''];
  const i = GRADE_ORDER.indexOf(key);
  return i >= 0 ? [0, String(i).padStart(2, '0')] : [1, key.toLowerCase()];
}
export const compareGrades = (a, b) => {
  const [ra, ka] = gradeSortKey(a);
  const [rb, kb] = gradeSortKey(b);
  return ra - rb || ka.localeCompare(kb);
};

/** URL slug: 'Pre-K' → 'pre-k', '1st Grade' → '1st-grade', NO_GRADE → 'no-grade'. */
export function gradeSlug(key) {
  if (key === NO_GRADE) return 'no-grade';
  return String(key).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
/** Inverse of gradeSlug over the known enum plus whatever grades are present. */
export function gradeFromSlug(slug, presentKeys = []) {
  if (!slug) return null;
  if (slug === 'no-grade') return NO_GRADE;
  const pool = [...GRADE_ORDER, ...presentKeys];
  return pool.find((g) => g !== NO_GRADE && gradeSlug(g) === slug) || null;
}

// Loose normalization for the mismatch note only (student.grade is free
// text from CSV imports: "K", "4K", "Pre-K"…). Display never uses this.
export function normalizeGradeText(g) {
  const s = String(g ?? '').trim().toLowerCase();
  if (!s) return '';
  if (/^(pre-?k|4k|pk|prek|prekindergarten)$/.test(s)) return 'Pre-K';
  if (/^(k|5k|kg|kindergarten)$/.test(s)) return 'Kindergarten';
  if (/^(1|1st|1st grade|first|first grade|grade 1)$/.test(s)) return '1st Grade';
  if (/^(2|2nd|2nd grade|second|second grade|grade 2)$/.test(s)) return '2nd Grade';
  if (/^(3|3rd|3rd grade|third|third grade|grade 3)$/.test(s)) return '3rd Grade';
  return String(g).trim();
}

// ─── Windows ─────────────────────────────────────────────────────────────────

export {
  WINDOW_LATEST, WINDOWS, isWindow, quarterLabelsForWindow,
  // v3.82.0 seasons (the feed's window mapping, shared through reportCore)
  SEASONS, isSeason, seasonOfQuarter, resolveSeason, fetchLabelsForWindow, resultsInWindow,
} from './reportCore';

const SEASON_LABELS = { fall: 'Fall', winter: 'Winter', spring: 'Spring' };
const QUARTER_WINDOWS = ['Q1', 'Q2', 'Q3', 'Q4'];

/** "2026–27" (en dash for display; pass '-' for a filename — makeSafeFilenamePart keeps a hyphen, not a dash). */
export const schoolYearShortLabel = (startYear, dash = '–') => `${startYear}${dash}${String(Number(startYear) + 1).slice(-2)}`;

/**
 * Window label. Quarters and Latest as before; a season reads "Fall 2026–27"
 * when the school year is given (page header, print title), "Fall" otherwise
 * (the selector, where the header already carries the year).
 */
export const windowLabel = (w, startYear = null) => {
  if (w === WINDOW_LATEST) return 'Latest (all quarters)';
  if (isSeason(w)) return startYear ? `${SEASON_LABELS[w]} ${schoolYearShortLabel(startYear)}` : SEASON_LABELS[w];
  return `Quarter ${w.slice(1)}`;
};

/** The window part of an export filename: 'Q1' / 'latest' as before; a season as "Fall 2026-27" (hyphen). */
export const windowFilePart = (w, startYear) => (isSeason(w) ? `${SEASON_LABELS[w]} ${schoolYearShortLabel(startYear, '-')}` : w);

/**
 * The Class Report's windows (v3.82.0): the three seasons, the four quarters
 * (so ?window=Q1 bookmarks and the harness pin still work) and Latest. Only
 * the Class Report offers seasons; the other selectors keep WINDOWS.
 */
export const CLASS_REPORT_WINDOWS = [...SEASONS, ...QUARTER_WINDOWS, WINDOW_LATEST];
export const isClassReportWindow = (w) => CLASS_REPORT_WINDOWS.includes(w);
/** The selector's optgroups: Season / Quarter / Latest (a null label renders ungrouped). */
export const CLASS_REPORT_WINDOW_GROUPS = [
  { label: 'Season', options: SEASONS.map((s) => ({ value: s, label: SEASON_LABELS[s] })) },
  { label: 'Quarter', options: QUARTER_WINDOWS.map((q) => ({ value: q, label: windowLabel(q) })) },
  { label: null, options: [{ value: WINDOW_LATEST, label: windowLabel(WINDOW_LATEST) }] },
];

/**
 * The window Results would default to today: the calendar quarter whose
 * start..end contains `now` (this year, then last year — Results.js
 * getCurrentQuarter), as 'Q1'..'Q4'; 'Q1' when no quarter contains today.
 */
export function currentQuarterWindow(quartersData = {}, now = new Date()) {
  const currentYear = now.getFullYear();
  for (const year of [String(currentYear), String(currentYear - 1)]) {
    const yq = quartersData[year] || {};
    for (const q of Object.keys(yq)) {
      const { start, end } = yq[q] || {};
      if (start && end && now >= new Date(start) && now <= new Date(end)) return `Q${q}`;
    }
  }
  return 'Q1';
}

/** The Class Report's default window (v3.82.0): the season of the quarter Results would default to. */
export function currentSeasonWindow(quartersData = {}, now = new Date()) {
  return SEASON_OF_QUARTER[Number(currentQuarterWindow(quartersData, now).slice(1))];
}

// ─── Columns (families in sidebar order) ─────────────────────────────────────

// `flag` (v3.82.0) = the feature flag that must be on for the family to be
// part of the FULL catalogue (catalogueColumns); the data-driven columnsFor
// never consults it — a result that exists is always shown, EXCEPT a family
// the teacher hid in Assessment Settings on her own Class Report (v3.85.1:
// columnsFor's `hiddenGroups`; hidden means hidden — table, print, exports).
export const FAMILIES = [
  { id: 'pa', label: PA_SCREENER_LABEL, types: PA_STEP_TYPES, flag: 'paScreener' },
  { id: 'pa4k', label: PA4K_GROUP_NAME, types: PA4K_TEST_TYPES, flag: 'pa4kAssessments' },
  { id: 'pad', label: 'PA Diagnostic', dynamic: true, flag: 'paDiagnostic' },
  // `observations` (v3.85.4): the Yes / No teacher observation shown on the
  // CLASS REPORT only (columnsFor / catalogueColumns `observations: true`) —
  // the TLS Grade Report, School View and Grade Team never pass it.
  { id: 'boy', label: '4K Readiness', types: ['boy_shapes', 'boy_numbers', 'boy_colors'], observations: ['boy_name_writing'], flag: 'boyReadiness' },
  { id: 'letter', label: 'Letter/Sound', types: ['uppercase', 'lowercase', 'sounds', ...VOWEL_TEST_TYPES], always: ['uppercase', 'lowercase', 'sounds'] },
  { id: 'hfw', label: 'High Frequency Words', types: [HFW_TEST_TYPE] },
  { id: 'cvc', label: 'CVC Words', types: [CVC_TEST_TYPE] },
  { id: 'nwf', label: 'Nonsense Word Fluency', types: ['nonsense_word'], flag: 'benchmarkScreener' },
];

const FAMILY_OF_TYPE = {};
FAMILIES.forEach((f) => [...(f.types || []), ...(f.observations || [])].forEach((t) => { FAMILY_OF_TYPE[t] = f; }));

/** v3.85.4: a Yes / No observation column (4K Readiness Name Writing) — a 1-item doc, Yes ≡ correctCount 1. */
const OBSERVATION_KEYS = new Set(FAMILIES.flatMap((f) => f.observations || []));
export const isObservationColumn = (key) => OBSERVATION_KEYS.has(key);
export const OBSERVATION_YES = 'Yes';
export const OBSERVATION_NO = 'No';
export const observationText = (score) => (Number(score) === 1 ? OBSERVATION_YES : OBSERVATION_NO);
/** Filter value for a Yes / No cell: 'text:Yes' / 'text:No'. */
export const textFilterKey = (text) => `text:${text}`;

/** Column key for a normalized result: the testType, or `pa_diagnostic:<subskill>` (reportCore). */
export { columnKeyOf } from './reportCore';

function padSubskillLabel(doc) {
  try {
    const tier = loadTier(doc.padTierId);
    return tier.subskills.find((s) => s.id === doc.padSubskillId)?.label || doc.padSubskillId;
  } catch {
    return doc?.padSubskillId || 'PA Diagnostic';
  }
}

// Single-column families get a short column label under their family header.
const SHORT_COLUMN_LABELS = { [HFW_TEST_TYPE]: 'HFW', [CVC_TEST_TYPE]: 'CVC', nonsense_word: 'NWF' };

export function columnLabel(key, sample) {
  if (SHORT_COLUMN_LABELS[key]) return SHORT_COLUMN_LABELS[key];
  if (key.startsWith(`${PAD_TEST_TYPE}:`)) return sample?.doc ? padSubskillLabel(sample.doc) : key.split(':')[1];
  return TEST_TYPE_LABELS[key] || key;
}

const familyOfKey = (key) => (key.startsWith(`${PAD_TEST_TYPE}:`) ? FAMILIES.find((f) => f.id === 'pad') : FAMILY_OF_TYPE[key] || null);

/**
 * The columns for a scope, data-driven, in family order. `present` = column
 * keys with at least one latest result in the window (Map key → sample).
 * `presentOnly` (v3.82.0, the Class Report): the Letter/Sound `always`
 * columns too appear only when present — an assessment nobody in the scope
 * has a score for is dropped (its name goes to the hidden-assessment note).
 * `hiddenGroups` (v3.85.1, the Class Report): the sidebarGroupsHidden names
 * (= these families' labels) — a hidden family gets NO column even when it
 * has scores. The Grade Report never passes it: hiding is one teacher's own
 * choice for her own pages.
 */
export function columnsFor(present, { presentOnly = false, hiddenGroups = [], observations = false } = {}) {
  const vowelsOn = new Set(enabledVowelTestTypes());
  const hidden = new Set(hiddenGroups);
  const out = [];
  for (const family of FAMILIES) {
    if (hidden.has(family.label)) continue;
    if (family.dynamic) {
      const keys = [...present.keys()].filter((k) => familyOfKey(k)?.id === family.id).sort();
      keys.forEach((k) => out.push({ key: k, label: columnLabel(k, present.get(k)), family: family.id, familyLabel: family.label }));
      continue;
    }
    for (const t of family.types) {
      if (VOWEL_TEST_TYPES.includes(t) && !vowelsOn.has(t)) continue;
      const always = !presentOnly && (family.always || []).includes(t);
      if (always || present.has(t)) out.push({ key: t, label: columnLabel(t), family: family.id, familyLabel: family.label });
    }
    // v3.85.4: the family's Yes / No observations, after its scored checks —
    // only for a caller that asks (the Class Report), only when present.
    if (observations) {
      for (const t of family.observations || []) {
        if (present.has(t)) out.push({ key: t, label: columnLabel(t), family: family.id, familyLabel: family.label });
      }
    }
  }
  return out;
}

/**
 * Labels of the families in `hiddenGroups` that DO have a result in `present`
 * (v3.85.1) — what the Class Report leaves out because the teacher hid it, so
 * the page can say so instead of looking like the scores were lost. Family
 * (= report) order.
 */
export function hiddenFamiliesWithScores(present, hiddenGroups = []) {
  const hidden = new Set(hiddenGroups);
  if (hidden.size === 0) return [];
  const withScores = new Set([...present.keys()].map((k) => familyOfKey(k)?.id).filter(Boolean));
  return FAMILIES.filter((f) => hidden.has(f.label) && withScores.has(f.id)).map((f) => f.label);
}

/**
 * The FULL column catalogue for a scope (v3.82.0), regardless of results:
 * every flag-enabled family's columns in report order — vowels per their
 * flag; the PA-D sub-skills of the tiers the given grades default to
 * (gradeToTier) plus any tier a result in `results` carries — minus the
 * families the teacher hid in Assessment Settings (`hiddenGroups` = the
 * sidebarGroupsHidden names, which are these families' labels). The Class
 * Report's hidden-assessment note reads it (hiddenAssessmentNames); PR B's
 * export layout will list it so a sheet's blank column can be kept.
 */
export function catalogueColumns({ grades = [], hiddenGroups = [], results = [], observations = false } = {}) {
  const hidden = new Set(hiddenGroups);
  const vowelsOn = new Set(enabledVowelTestTypes());
  const out = [];
  const seen = new Set();
  const push = (c) => { if (!seen.has(c.key)) { seen.add(c.key); out.push(c); } };
  for (const family of FAMILIES) {
    if (family.flag && !isFeatureEnabled(family.flag)) continue;
    if (hidden.has(family.label)) continue;
    if (family.dynamic) {
      const tierIds = new Set(grades.map((g) => gradeToTier(g)).filter(Boolean));
      results.forEach((r) => { if (r?.testType === PAD_KEY && r.doc?.padTierId) tierIds.add(r.doc.padTierId); });
      for (const tierId of PAD_TIER_IDS.filter((id) => tierIds.has(id))) {
        let tier;
        try { tier = loadTier(tierId); } catch { continue; }
        tier.subskills.forEach((sub) => push({ key: `${PAD_KEY}:${sub.id}`, label: sub.label, family: family.id, familyLabel: family.label }));
      }
      continue;
    }
    for (const t of family.types) {
      if (VOWEL_TEST_TYPES.includes(t) && !vowelsOn.has(t)) continue;
      push({ key: t, label: columnLabel(t), family: family.id, familyLabel: family.label });
    }
    if (observations) (family.observations || []).forEach((t) => push({ key: t, label: columnLabel(t), family: family.id, familyLabel: family.label }));
  }
  return out;
}

/**
 * Names for the hidden-assessment note (v3.82.0): the catalogue columns
 * absent from `columns`, family-granular — a family with no column shown is
 * named once ("CVC Words"); a partly shown one lists its missing columns
 * ("Letter/Sound: Long Vowels, Short Vowels"). Catalogue (= report) order.
 */
export function hiddenAssessmentNames(catalogue, columns) {
  const shown = new Set(columns.map((c) => c.key));
  const byFamily = new Map();
  catalogue.forEach((c) => {
    if (!byFamily.has(c.family)) byFamily.set(c.family, { label: c.familyLabel, cols: [] });
    byFamily.get(c.family).cols.push(c);
  });
  const out = [];
  for (const { label, cols } of byFamily.values()) {
    const missing = cols.filter((c) => !shown.has(c.key));
    if (missing.length === 0) continue;
    out.push(missing.length === cols.length ? label : `${label}: ${missing.map((c) => c.label).join(', ')}`);
  }
  return out;
}

// ─── Results ─────────────────────────────────────────────────────────────────

// normalizeResult (one raw doc per collection; an N/A doc is not a result) and
// pickLatestInWindow (higher quarter label, then timestamp) — reportCore.js.
export { normalizeResult, pickLatestInWindow } from './reportCore';

const bandOf = (r) => bandForScore(r.testType, r.score, r.max, { doc: r.doc });

// Per-column summary for a set of students: administered / total / bands.
// The legend reads on the family's legend scale (legendMaxFor: the letter
// tests always on 26 — a consonants-only run's 21 becomes a `scaleNote`
// line, v3.85.0 — every other family on the doc's own max); each cell still
// bands on its own max (bandOf).
function summarizeColumns(studentIds, latestByKey, columns) {
  const out = {};
  for (const col of columns) {
    const testType = col.key.split(':')[0];
    const bands = {};
    let administered = 0;
    let legend = null;
    let maxSeen = null;
    const maxes = [];
    for (const sid of studentIds) {
      const r = latestByKey.get(`${sid}|${col.key}`);
      if (!r) continue;
      administered += 1;
      if (maxSeen === null && r.max) maxSeen = r.max;
      if (r.max) maxes.push(r.max);
      if (!legend) legend = bandsForTestType(r.testType, legendMaxFor(r.testType, r.max), { doc: r.doc });
      const b = bandOf(r);
      if (b) bands[b.key] = (bands[b.key] || 0) + 1;
    }
    if (!legend) legend = bandsForTestType(testType, legendMaxFor(testType, maxSeen || undefined));
    // Every legend band is present (0 when empty) so the UI never hardcodes a count.
    legend.forEach((b) => { if (bands[b.key] === undefined) bands[b.key] = 0; });
    const scaleNote = LETTER_TEST_TYPES.includes(testType) ? letterScaleNote(maxes) : null;
    out[col.key] = { administered, total: studentIds.length, bands, legend, max: maxSeen === null ? null : legendMaxFor(testType, maxSeen), scaleNote };
  }
  return out;
}

function presentColumns(latestByKey, studentIdSet) {
  const present = new Map();
  for (const [k, r] of latestByKey) {
    const sid = k.slice(0, k.indexOf('|'));
    if (!studentIdSet.has(sid)) continue;
    const col = columnKeyOf(r);
    if (!present.has(col)) present.set(col, r);
  }
  return present;
}

/** { firstName, lastName } for a student doc: its own fields, else the display name split as lastNameComparator splits it. */
export function studentNameParts(s) {
  const first = typeof s?.firstName === 'string' ? s.firstName.trim() : '';
  const last = typeof s?.lastName === 'string' ? s.lastName.trim() : '';
  if (first || last) return { firstName: first, lastName: last };
  const parts = studentDisplayName(s).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

/** "Last, First" for a report row (a single-word name shows as is). */
export const lastFirstName = (row) => {
  const first = row?.firstName || '';
  const last = row?.lastName || '';
  if (first && last) return `${last}, ${first}`;
  return last || first || row?.displayName || '';
};

const teacherNameFor = (cls, teacherById) => {
  const t = cls?.teacherId ? teacherById?.[cls.teacherId] : null;
  return t?.displayName || t?.email || cls?.homeroomTeacher || '';
};

/**
 * The summary object (see the header). `results` are normalized results.
 */
export function deriveSchoolSummary({ classes, students, results, window, startYear, teacherById = {} }) {
  const labels = quarterLabelsForWindow(startYear, window);
  const latest = pickLatestInWindow(results, labels);
  const labelMap = buildClassLabelMap(classes);
  const classById = Object.fromEntries(classes.map((c) => [c.id, c]));
  const studentsByClass = {};
  students.forEach((s) => { if (classById[s.classId]) (studentsByClass[s.classId] = studentsByClass[s.classId] || []).push(s); });

  const sortedClasses = [...classes].sort((a, b) =>
    compareGrades(gradeKeyOf(a), gradeKeyOf(b)) || classLabelFrom(labelMap, a.id).localeCompare(classLabelFrom(labelMap, b.id)));

  const allStudentIds = new Set(students.filter((s) => classById[s.classId]).map((s) => s.id));
  const columns = columnsFor(presentColumns(latest, allStudentIds));

  const classBlocks = sortedClasses.map((c) => {
    const roster = studentsByClass[c.id] || [];
    const ids = roster.map((s) => s.id);
    return {
      classId: c.id,
      displayName: classLabelFrom(labelMap, c.id),
      grade: gradeKeyOf(c),
      teacherName: teacherNameFor(c, teacherById),
      studentCount: ids.length,
      // A class block carries its own present columns (+ U/L/S), like a grade block.
      assessments: summarizeColumns(ids, latest, columnsFor(presentColumns(latest, new Set(ids)))),
    };
  });

  const grades = {};
  for (const c of sortedClasses) {
    const key = gradeKeyOf(c);
    const roster = studentsByClass[c.id] || [];
    const g = grades[key] || (grades[key] = { grade: key, label: gradeLabel(key), classCount: 0, studentCount: 0, classIds: [], studentIds: [], mismatchCount: 0 });
    g.classCount += 1;
    g.classIds.push(c.id);
    g.studentCount += roster.length;
    g.studentIds.push(...roster.map((s) => s.id));
    if (key !== NO_GRADE) {
      g.mismatchCount += roster.filter((s) => s.grade && normalizeGradeText(s.grade) !== normalizeGradeText(key)).length;
    }
  }
  Object.values(grades).forEach((g) => {
    g.assessments = summarizeColumns(g.studentIds, latest, columnsFor(presentColumns(latest, new Set(g.studentIds))));
    delete g.studentIds;
  });

  return {
    window,
    labels,
    columns,
    // Building-wide roll-up: feeds the legend (max + cut points per column).
    building: { studentCount: allStudentIds.size, assessments: summarizeColumns([...allStudentIds], latest, columns) },
    classes: classBlocks,
    grades,
    gradeOrder: Object.keys(grades).sort(compareGrades),
    resultCount: latest.size,
  };
}

/**
 * Report rows for a set of classes (v3.79.0 — the private core both report
 * wrappers share): one row per student across the classes in `classIds`, the
 * data-driven column catalog for that scope (U/L/S always), the latest result
 * per (student, column) in the window, null when not administered (an N/A doc
 * is not a result). Everything scope-specific is the caller's: the Grade
 * Report passes its grade's classes, the Class Report the teacher's own.
 */
function deriveReportRows({ classes, students, results, window, startYear, classIds, teacherById = {}, presentOnly = false, hiddenGroups = [], observations = false }) {
  const labels = quarterLabelsForWindow(startYear, window);
  const scopeClasses = classes.filter((c) => classIds.has(c.id));
  const scopeIds = new Set(scopeClasses.map((c) => c.id));
  const labelMap = buildClassLabelMap(classes);
  const roster = students.filter((s) => scopeIds.has(s.classId));
  // v3.82.0: membership by resultsInWindow (a season honours a screener doc's
  // own window stamp), then the newest attempt over the fetched label set —
  // for a quarter window or Latest this is exactly the pre-3.82.0 rule.
  const inWindow = resultsInWindow(results.filter((r) => scopeIds.has(r.classId)), startYear, window);
  const latest = pickLatestInWindow(inWindow, fetchLabelsForWindow(startYear, window));
  const present = presentColumns(latest, new Set(roster.map((s) => s.id)));
  const columns = columnsFor(present, { presentOnly, hiddenGroups, observations });

  const rows = roster.map((s) => {
    const cells = {};
    for (const col of columns) {
      const r = latest.get(`${s.id}|${col.key}`);
      cells[col.key] = r
        ? {
          band: bandOf(r), score: r.score, max: r.max, date: r.timestamp, quarter: r.quarter, attemptCount: r.attemptCount,
          // v3.85.4: a Yes / No observation shows its answer, never "1/1".
          ...(isObservationColumn(col.key) ? { text: observationText(r.score) } : {}),
        }
        : null;
    }
    const { firstName, lastName } = studentNameParts(s);
    return {
      studentId: s.id,
      displayName: studentDisplayName(s),
      name: studentDisplayName(s),
      // v3.83.0: plain name parts for the Student picker ("Last, First") and
      // PR B's export columns — from the student doc's fields when present,
      // else split the way lastNameComparator splits (last word = last name).
      firstName,
      lastName,
      // v3.84.0 (export layout): the district id (students.localStudentId —
      // the Clever SIS id, text, leading zeros kept; '' when absent) and the
      // class's grade label, for the Student ID / Grade export columns.
      localStudentId: s.localStudentId === undefined || s.localStudentId === null ? '' : String(s.localStudentId),
      grade: (() => { const g = scopeClasses.find((c) => c.id === s.classId)?.grade; return typeof g === 'string' ? g.trim() : ''; })(),
      classId: s.classId,
      className: classLabelFrom(labelMap, s.classId),
      teacherName: teacherNameFor(scopeClasses.find((c) => c.id === s.classId), teacherById),
      cells,
    };
  });

  return {
    window,
    labels,
    columns,
    // v3.85.1: families the viewer hid in Assessment Settings that have scores
    // in this scope and window (always [] when no hiddenGroups are passed).
    hiddenWithScores: hiddenFamiliesWithScores(present, hiddenGroups),
    rows,
    classes: scopeClasses.map((c) => ({ classId: c.id, displayName: classLabelFrom(labelMap, c.id) }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    classCount: scopeClasses.length,
    studentCount: roster.length,
  };
}

/**
 * Grade Report rows: one per student in the grade, across all its classes.
 */
export function deriveGradeReport({ classes, students, results, window, startYear, grade, teacherById = {} }) {
  const classIds = new Set(classes.filter((c) => gradeKeyOf(c) === grade).map((c) => c.id));
  const base = deriveReportRows({ classes, students, results, window, startYear, classIds, teacherById });
  return { grade, label: gradeLabel(grade), ...base };
}

/** Header label for a class-scoped report: the class names, or a count past three. */
export function classReportLabel(classes) {
  if (classes.length === 0) return 'No classes';
  if (classes.length <= 3) return classes.map((c) => c.displayName).join(' · ');
  return `${classes.length} classes`;
}

/**
 * Class Report rows (v3.79.0): the teacher's own classes — `classIds` (Set or
 * array; omitted = every class given) instead of a grade. Same row shape,
 * same columns-present rule, same "latest in window", same not-administered
 * ≠ zero as the Grade Report, by construction (one core above).
 * `hiddenGroups` (v3.85.1): the viewer's sidebarGroupsHidden — those families
 * get no column, so they are absent from the table, the print and every export.
 */
export function deriveClassReport({ classes, students, results, window, startYear, classIds = null, teacherById = {}, presentOnly = false, hiddenGroups = [], observations = false }) {
  const ids = classIds ? new Set(classIds) : new Set(classes.map((c) => c.id));
  const base = deriveReportRows({ classes, students, results, window, startYear, classIds: ids, teacherById, presentOnly, hiddenGroups, observations });
  return { grade: null, label: classReportLabel(base.classes), ...base };
}

/** Sort rows: 'class' = class label then last name (default); 'name' = last name. */
export function sortReportRows(rows, sort = 'class') {
  const byName = (a, b) => lastNameComparator(a, b);
  return [...rows].sort(sort === 'name' ? byName : (a, b) => a.className.localeCompare(b.className) || byName(a, b));
}

/**
 * Filters: `classIds` (Set or null = all), `bandKey` (only rows with at least
 * one cell in that band, or in `column` when given).
 */
export function filterReportRows(rows, { classIds = null, bandKey = null, column = null } = {}) {
  return rows.filter((row) => {
    if (classIds && classIds.size > 0 && !classIds.has(row.classId)) return false;
    if (!bandKey) return true;
    const cells = column ? [row.cells[column]] : Object.values(row.cells);
    return cells.some((c) => c && c.band && c.band.key === bandKey);
  });
}

/**
 * CSV rows for a report — band label or score per the display mode. Serves
 * BOTH the Grade Report and the Class Report (v3.79.0): it reads only
 * `report.columns` and the rows it is given, never the scope.
 */
export function gradeReportCsv(report, rows, mode = 'band') {
  const fields = ['student', 'class', ...report.columns.map((c) => `${c.familyLabel}: ${c.label}`)];
  const data = rows.map((row) => [
    safeCsvCell(row.displayName),
    safeCsvCell(row.className),
    ...report.columns.map((c) => {
      const cell = row.cells[c.key];
      if (!cell) return '';
      if (mode === 'score') return cell.max ? `${cell.score}/${cell.max}` : `${cell.score}`;
      return cell.band ? cell.band.label : `${cell.score}${cell.max ? `/${cell.max}` : ''}`;
    }),
  ]);
  return { fields, rows: data, rowCount: data.length, dateOf: formatDateForCsv };
}

// ─── Column sort + filter (v3.82.0, the Class Report's header controls) ──────
// Opt-in through ReportTable's `columnControls`; the Grade Report never passes
// it and Grade Team never renders ReportTable (docs/requirements/grade-team-
// view.md: classes are never ranked by score or band).

export const STUDENT_COLUMN = 'student';
export const CLASS_COLUMN = 'class';
/** Filter option keys beside the band keys. */
export const FILTER_NA = 'na';               // "Not administered" ("—")
export const FILTER_ASSESSED = 'assessed';   // a bandless column's cells (4K Readiness)
export const FILTER_UNBANDED = 'unbanded';   // a score with no level (a partial PA-D doc)

// Fallback ladder for a band key missing from the column's legend (PA-D's
// legend needs the doc; its cells still carry the stored band key).
const BAND_LADDER = { needs_support: 0, developing: 0, emerging: 1, mastery: 2, proficient: 2 };

/** Band rank for sorting: reverse legend index (lowest band = 0), else the ladder. */
export function bandRank(bandKey, legend = []) {
  const i = legend.findIndex((b) => b.key === bandKey);
  if (i >= 0) return legend.length - 1 - i;
  return BAND_LADDER[bandKey] ?? -1;
}

/** The column's legend from its first administered cell (the BandLegend's source); [] when unknown or bandless. */
export function columnLegend(report, key) {
  const cell = report.rows.map((r) => r.cells[key]).find(Boolean);
  const testType = key.split(':')[0];
  return cell ? bandsForTestType(testType, legendMaxFor(testType, cell.max), { doc: cell.doc }) : [];
}

/** The v3.85.0 legend note for a column ("Consonants only: 18 / 10 of 21"), or null. */
export function columnScaleNote(report, key) {
  const testType = key.split(':')[0];
  if (!LETTER_TEST_TYPES.includes(testType)) return null;
  return letterScaleNote(report.rows.map((r) => r.cells[key]).filter(Boolean).map((c) => c.max));
}

const hasBandedCell = (report, key) => report.rows.some((r) => r.cells[key] && r.cells[key].band);

/**
 * Sort rows by one column. `sort` = { key, dir: 'asc' | 'desc' } or null
 * (= the default class → last-name order). Student / class columns sort
 * alphabetically; an assessment column by band rank (lowest first on asc) in
 * Mastery mode and by raw score in Score mode — a bandless column by score in
 * both; a cell with a score but no level ranks below every band. "—" (not
 * administered) is LAST in either direction. Ties: last name, then first name
 * (lastNameComparator), always ascending. Stable.
 */
export function sortRowsByColumn(rows, sort, report, mode = 'band') {
  if (!sort || !sort.key) return sortReportRows(rows, 'class');
  const dir = sort.dir === 'desc' ? -1 : 1;
  const byName = (a, b) => lastNameComparator(a, b);
  if (sort.key === STUDENT_COLUMN) return [...rows].sort((a, b) => dir * byName(a, b));
  if (sort.key === CLASS_COLUMN) return [...rows].sort((a, b) => dir * (a.className || '').localeCompare(b.className || '') || byName(a, b));
  const legend = report ? columnLegend(report, sort.key) : [];
  const byScore = mode === 'score' || !report || !hasBandedCell(report, sort.key);
  const valueOf = (row) => {
    const c = row.cells[sort.key];
    if (!c) return null;
    // Score mode sorts by PERCENT of the cell's own max (v3.85.0 — a 21/21
    // consonants-only run sorts above a 22/26), the raw score is what shows.
    if (byScore) return Number(c.max) > 0 ? Number(c.score) / Number(c.max) : Number(c.score);
    return c.band ? bandRank(c.band.key, legend) : -1;
  };
  return [...rows].sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va === null && vb === null) return byName(a, b);
    if (va === null) return 1;
    if (vb === null) return -1;
    return dir * (va - vb) || byName(a, b);
  });
}

const valuesOf = (f) => (f && Array.isArray(f.values) ? f.values : []);
const isActiveFilter = (f) => valuesOf(f).length > 0;
// The Class column is not a filter key any more (v3.83.0): the class funnel
// drives the page's class-chip selection (one state, two controls).
export const hasActiveFilters = (filters = {}) => Object.entries(filters || {}).some(([key, f]) => key !== CLASS_COLUMN && isActiveFilter(f));

/**
 * Column filters, ANDed: `filters` = { [columnKey]: { values } }.
 * Student (v3.83.0): the picked student ids. An assessment column: the
 * cell's band key must be checked (Score mode filters by the same band —
 * bandForScore — the chip would show), FILTER_NA matches "—", FILTER_ASSESSED
 * / FILTER_UNBANDED match a cell with a score and no level. A filter on a
 * column the report does not have is ignored (the window changed under it);
 * a Class key is ignored (the chips own that selection).
 */
export function filterRowsByColumns(rows, filters = {}, report = null) {
  const known = report ? new Set(report.columns.map((c) => c.key)) : null;
  const active = Object.entries(filters || {}).filter(([key, f]) => isActiveFilter(f)
    && key !== CLASS_COLUMN && (key === STUDENT_COLUMN || !known || known.has(key)));
  if (active.length === 0) return rows;
  return rows.filter((row) => active.every(([key, f]) => {
    const values = valuesOf(f);
    if (key === STUDENT_COLUMN) return values.includes(row.studentId);
    const cell = row.cells[key];
    if (!cell) return values.includes(FILTER_NA);
    if (cell.text) return values.includes(textFilterKey(cell.text)); // a Yes / No observation (v3.85.4)
    if (cell.band) return values.includes(cell.band.key);
    return values.includes(FILTER_ASSESSED) || values.includes(FILTER_UNBANDED);
  }));
}

/**
 * The Student picker's list (v3.83.0): every student of the rows given — the
 * selected class(es) BEFORE column filters, so a student can be picked
 * whatever else is filtered — as { key: studentId, label: "Last, First" },
 * sorted by last name then first name (lastNameComparator).
 */
export function studentOptionsFor(rows) {
  return [...rows].sort(lastNameComparator).map((r) => ({ key: r.studentId, label: lastFirstName(r) }));
}

/**
 * Checklist options for an assessment column: the band labels present in the
 * column (best band first), "Assessed, no level" / "Assessed" when such cells
 * exist, and "Not administered" last (always offered).
 */
export function filterOptionsForColumn(report, key) {
  const cells = report.rows.map((r) => r.cells[key]).filter(Boolean);
  // v3.85.4: a Yes / No observation column filters by its answers (Yes first).
  if (cells.some((c) => c.text)) {
    const texts = new Set(cells.map((c) => c.text).filter(Boolean));
    return [
      ...[OBSERVATION_YES, OBSERVATION_NO].filter((t) => texts.has(t)).map((t) => ({ key: textFilterKey(t), label: t })),
      { key: FILTER_NA, label: 'Not administered' },
    ];
  }
  const legend = columnLegend(report, key);
  const banded = cells.some((c) => c.band);
  const labels = new Map();
  cells.forEach((c) => { if (c.band && !labels.has(c.band.key)) labels.set(c.band.key, c.band.label); });
  const out = [...labels.keys()]
    .sort((a, b) => bandRank(b, legend) - bandRank(a, legend))
    .map((k) => ({ key: k, label: labels.get(k) }));
  if (cells.some((c) => !c.band)) out.push(banded ? { key: FILTER_UNBANDED, label: 'Assessed, no level' } : { key: FILTER_ASSESSED, label: 'Assessed' });
  out.push({ key: FILTER_NA, label: 'Not administered' });
  return out;
}

/** The active filters as chip labels: [{ key, label }] — 'Student: 3 selected' / 'Student: Zed, Ava', 'Uppercase Letters: Mastery, Emerging'. */
export function describeFilters(report, filters = {}) {
  const out = [];
  for (const [key, f] of Object.entries(filters || {})) {
    if (!isActiveFilter(f) || key === CLASS_COLUMN) continue;
    if (key === STUDENT_COLUMN) {
      const ids = valuesOf(f);
      const one = ids.length === 1 ? report?.rows.find((r) => r.studentId === ids[0]) : null;
      out.push({ key, label: one ? `Student: ${lastFirstName(one)}` : `Student: ${ids.length} selected` });
      continue;
    }
    const col = report?.columns.find((c) => c.key === key);
    if (!col) continue;
    const labels = filterOptionsForColumn(report, key).filter((o) => valuesOf(f).includes(o.key)).map((o) => o.label);
    if (labels.length > 0) out.push({ key, label: `${col.label}: ${labels.join(', ')}` });
  }
  return out;
}
